package br.com.htms.inspeccaocampo.export

import br.com.htms.inspeccaocampo.data.funcionarios.DocumentoResumo
import br.com.htms.inspeccaocampo.data.funcionarios.DocumentoTipo
import br.com.htms.inspeccaocampo.data.funcionarios.Funcionario

/** Emissão de Documentos (Funcionários): cadastro, documentos arquivados e dashboard. */
object PlanilhaFuncionarios {
    private fun origem(o: String) = if (o == "EMITIDO") "Emitido pelo portal" else "Enviado (arquivo)"

    fun build(funcionarios: List<Funcionario>, docs: List<DocumentoResumo>): ByteArray {
        val lista = funcionarios.sortedBy { it.nome.lowercase() }
        val porFunc = docs.groupBy { it.funcionarioId }
        val porId = lista.associateBy { it.id }
        fun ativo(f: Funcionario) = f.situacao.trim().uppercase().startsWith("ATIVO")

        val cols = buildList {
            add(ColPlan("NOME", 38)); add(ColPlan("CPF", 15)); add(ColPlan("MATRÍCULA", 11)); add(ColPlan("VÍNCULO", 9))
            add(ColPlan("SITUAÇÃO", 14)); add(ColPlan("CARGO", 32)); add(ColPlan("SETOR / GHE", 18))
            add(ColPlan("ADMISSÃO", 12, TipoCol.DATA)); add(ColPlan("ANO DE ADMISSÃO", 10)); add(ColPlan("NASCIMENTO", 12, TipoCol.DATA))
            add(ColPlan("RG", 14)); add(ColPlan("TOTAL DE DOCUMENTOS", 12, TipoCol.NUMERO))
            DocumentoTipo.todos.forEach { add(ColPlan(it.second.uppercase(), 11, TipoCol.NUMERO)) }
            add(ColPlan("ÚLTIMO DOCUMENTO", 12, TipoCol.DATA))
        }
        val func = PlanKit.abaDados(
            "FUNCIONÁRIOS", "Funcionários · cadastro e documentos",
            "Um funcionário por linha, com a quantidade de documentos de cada tipo. Filtre e o DASHBOARD acompanha.",
            cols,
            lista.map { f ->
                val ds = porFunc[f.id].orEmpty()
                val adm = PlanData.de(f.admissao)
                buildList<Any?> {
                    add(f.nome); add(f.cpfFormatado); add(f.matricula.toIntOrNull() ?: f.matricula); add(f.vinculo.uppercase())
                    add(f.situacao.uppercase()); add(f.cargoNome); add(f.setorGhe)
                    add(adm); add(PlanData.mes(adm).take(4)); add(PlanData.de(f.nascimento)); add(f.rg)
                    add(if (ds.isEmpty()) f.totalDocumentos else ds.size)
                    DocumentoTipo.todos.forEach { (k, _) -> add(ds.count { it.tipo == k }.takeIf { it > 0 }) }
                    add(PlanData.deMs(ds.maxOfOrNull { it.createdAt }))
                }
            },
            cor = "1565C0"
        )
        val docsOrd = docs.sortedByDescending { it.createdAt }
        val doc = PlanKit.abaDados(
            "DOCUMENTOS", "Documentos arquivados", "Um documento por linha (emitidos pelo portal ou enviados).",
            listOf(
                ColPlan("DATA", 12, TipoCol.DATA), ColPlan("MÊS", 9), ColPlan("FUNCIONÁRIO", 38), ColPlan("CPF", 15),
                ColPlan("VÍNCULO", 9), ColPlan("CARGO", 30), ColPlan("TIPO", 20), ColPlan("ORIGEM", 20), ColPlan("POR", 22)
            ),
            docsOrd.map { d ->
                val f = porId[d.funcionarioId]
                val data = PlanData.deMs(d.createdAt)
                listOf(
                    data, PlanData.mes(data), f?.nome.orEmpty(), f?.cpfFormatado.orEmpty(), f?.vinculo?.uppercase().orEmpty(),
                    f?.cargoNome.orEmpty(), DocumentoTipo.rotulo(d.tipo), origem(d.origem), d.createdBy
                )
            },
            cor = "6A1B9A"
        )

        val hoje = PlanData.hojeSerial()
        val kpis = listOf(
            KpiPlan("Funcionários", func.contar(), lista.size.toDouble()),
            KpiPlan("Ativos", func.contarComeca("SITUAÇÃO", "ATIVO"), lista.count(::ativo).toDouble(), SharedOoxmlWriter.STYLE_CRIT_BAIXA),
            KpiPlan("CLT", func.contarSe("VÍNCULO", "CLT"), lista.count { it.vinculo.uppercase() == "CLT" }.toDouble()),
            KpiPlan("PJ", func.contarSe("VÍNCULO", "PJ"), lista.count { it.vinculo.uppercase() == "PJ" }.toDouble()),
            KpiPlan("Com documentos", func.contarPositivos("TOTAL DE DOCUMENTOS"),
                lista.count { (porFunc[it.id]?.size ?: it.totalDocumentos) > 0 }.toDouble()),
            KpiPlan("Sem documentos", "${func.contar()}-${func.contarPositivos("TOTAL DE DOCUMENTOS")}",
                lista.count { (porFunc[it.id]?.size ?: it.totalDocumentos) == 0 }.toDouble(), SharedOoxmlWriter.STYLE_CRIT_MEDIA),
            KpiPlan("Documentos", doc.contar(), docs.size.toDouble()),
            KpiPlan("Emitidos pelo portal", doc.contarSe("ORIGEM", origem("EMITIDO")), docs.count { it.origem == "EMITIDO" }.toDouble()),
            KpiPlan("Documentos (30 dias)", "SUMPRODUCT((${doc.faixa("DATA")}>=TODAY()-30)*${doc.visiveis()})",
                docsOrd.count { (PlanData.deMs(it.createdAt)?.serial ?: 0) >= hoje - 30 }.toDouble())
        )
        val blocos = listOf(
            BlocoPlan("Funcionários por vínculo", "Vínculo", PlanKit.contagem(lista) { it.vinculo.uppercase() },
                { func.contarSe("VÍNCULO", it) }, SharedExcelChartType.PIE),
            BlocoPlan("Funcionários por situação", "Situação", PlanKit.contagem(lista) { it.situacao.uppercase() },
                { func.contarSe("SITUAÇÃO", it) }, SharedExcelChartType.PIE),
            BlocoPlan("Funcionários por cargo", "Cargo", PlanKit.contagem(lista) { it.cargoNome },
                { func.contarSe("CARGO", it) }, formulaTotal = func.contar(), limite = 15),
            BlocoPlan("Documentos por tipo", "Tipo", DocumentoTipo.todos.map { (k, r) -> r to docs.count { it.tipo == k }.toDouble() }
                .filter { it.second > 0 }.sortedByDescending { it.second },
                { doc.contarSe("TIPO", it) }, SharedExcelChartType.PIE),
            BlocoPlan("Documentos por mês", "Mês", PlanKit.contagem(docsOrd) { PlanData.mes(PlanData.deMs(it.createdAt)) }.sortedBy { it.first },
                { doc.contarSe("MÊS", it) }, limite = 24),
            BlocoPlan("Documentos por origem", "Origem", PlanKit.contagem(docs) { origem(it.origem) },
                { doc.contarSe("ORIGEM", it) }, SharedExcelChartType.PIE),
            BlocoPlan("Documentos por cargo", "Cargo", PlanKit.contagem(docs) { porId[it.funcionarioId]?.cargoNome.orEmpty() },
                { doc.contarSe("CARGO", it) }, formulaTotal = doc.contar(), limite = 15)
        )
        val dash = PlanKit.dashboard(
            "DASHBOARD", "Dashboard · Emissão de Documentos",
            "Filtre as abas FUNCIONÁRIOS ou DOCUMENTOS e o dashboard acompanha. Gerado em ${PlanData.geradoEm()}.",
            kpis, blocos, cor = "4A148C"
        )
        return PlanKit.gerar(listOf(dash, func.sheet, doc.sheet))
    }
}
